import { useCallback, useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import {
  GithubRepoDescription,
  GithubRepoField,
  repoDescriptionValidation,
  repoNameValidation,
} from './GithubRepoField';
import { GithubTeamSelect, githubTeamValidation } from './../../../components/GithubTeamSelect';
import { Cluster, DataClassificationGroup, clusterValidation, DataClassification } from './Cluster';
import { Namespace, namespaceValidation } from './Namespace';
import { ContainerNameField, containerNameValidation } from './ContainerName';
import { CreateIngress, IngressOptions } from './CreateIngress';
import { FieldValidation } from '@rjsf/utils';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { ApiHolder, useApiHolder } from '@backstage/core-plugin-api';
import { getGithubDestination } from './githubDestination';
import { ReuseableWorkflow, rwSelectionOptions } from './ReuseableWorkflow';
import { CmdbAppShortName, SquadInfo, AIFoundrySelector, aiFoundrySelectorValidation } from '../../../components';
import { AADGroupNameDropdown } from '../../../components/AADGroupNameDropdown';
import type { AIFoundrySelectorOutput } from '../../../components/AIFoundrySelector/types';
import { HashicorpVault } from './HashicorpVault';
import { UseCaseCategory, validateUseCaseCategory } from '../UseCaseCategory';
import type { AIValidationResult } from '../AppShortnameAIValidator';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import { withStyles, makeStyles, createStyles } from '@material-ui/core/styles';
import Link from '@material-ui/core/Link';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import InfoIcon from '@material-ui/icons/Info';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles(() =>
  createStyles({
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    },
  }),
);

export type ReturnedData = {
  squad: {
    name: string;
    id: string;
  };
  shortName: string;
  archerId: string;
  cmdbAppFullName: string;
  costCenter: string;
  repoName: string;
  repoDescription: string;
  githubTeam: string;
  pci: string;
  cluster: string;
  namespace: string;
  containerName: string;
  containerPort: number;
  createIngress: IngressOptions;
  trafficPermissionPolicy: boolean;
  rwSelection: string;
  vaultNamespace: string;
  vaultSecretsPath: string;
  productName: string;
  useCaseCategory: string;
  aiFoundrySelection: AIFoundrySelectorOutput | null;
};

export const defaultValues = {
  squad: {
    name: '',
    id: '',
  },
  shortName: '',
  archerId: '',
  cmdbAppFullName: '',
  costCenter: '',
  repoName: '',
  repoDescription: '',
  githubTeam: '',
  pci: DataClassification.NON_PCI,
  cluster: '',
  namespace: '',
  containerName: '',
  containerPort: 8100,
  createIngress: IngressOptions.NONE,
  trafficPermissionPolicy: false,
  rwSelection: rwSelectionOptions.DEFAULT,
  vaultNamespace: '',
  vaultSecretsPath: '',
  productName: '',
  useCaseCategory: '',
  aiFoundrySelection: null,
};

export const ComponentInformation = ({
  onChange,
  formData,
  uiSchema,
  error,
  formContext,
}: FieldExtensionComponentProps<ReturnedData>) => {
  const classes = useStyles();
  const isWebsiteTemplate = Boolean(uiSchema?.['ui:options']?.isWebsite);
  const isAIAgentTemplate = Boolean(uiSchema?.['ui:options']?.isAIAgent);
  const aiValidationResult = isAIAgentTemplate
    ? (((formContext?.formData as Record<string, unknown>)?.appShortnameValidation as AIValidationResult | undefined) ??
      null)
    : null;
  const aiConfigData = aiValidationResult?.data ?? null;
  const apiHolder = useApiHolder();
  // The template option must match its publish owner; it is not inferred from the template URL.
  const githubDestination = getGithubDestination(uiSchema?.['ui:options']?.githubOrganization, apiHolder);
  const destination = githubDestination.status === 'resolved' ? githubDestination.destination : undefined;
  const [returnedData, setReturnedData] = useState<ReturnedData>({
    ...defaultValues,
    ...(formData || {}),
    ...(isWebsiteTemplate ? { createIngress: IngressOptions.AKS_PUBLIC } : {}),
    containerPort: (uiSchema?.['ui:options']?.defaultPort as number) ?? 8100,
  });

  const NoPaddingTooltip = withStyles({
    tooltip: {
      padding: 0,
      margin: 5,
      boxShadow: 'none',
      border: 'none',
    },
  })(Tooltip);

  useEffect(() => {
    onChange({ ...returnedData });
  }, [onChange, returnedData]);

  const handleChangeAks = (selectedIngressOption: IngressOptions) => {
    setReturnedData(prevReturnedData => ({
      ...prevReturnedData,
      createIngress: selectedIngressOption,
      trafficPermissionPolicy: selectedIngressOption === IngressOptions.AKS_PUBLIC,
    }));
  };

  const handleGithubTeamChange = useCallback((values: string) => {
    setReturnedData(prevReturnedData => ({
      ...prevReturnedData,
      githubTeam: values,
    }));
  }, []);

  const handleChangePci = (event: string) => {
    setReturnedData(prevReturnedData => ({
      ...prevReturnedData,
      pci: event,
    }));
  };

  const handleRWOption = (event: string) => {
    setReturnedData(prevReturnedData => ({
      ...prevReturnedData,
      rwSelection: event,
    }));
  };

  const handleHashicorpVault = (vaultInfo: { vaultNamespace: string; vaultPath: string }) => {
    setReturnedData(prevReturnedData => ({
      ...prevReturnedData,
      vaultNamespace: vaultInfo.vaultNamespace,
      vaultSecretsPath: vaultInfo.vaultPath,
    }));
  };

  const handleAIFoundryChange = (selection: AIFoundrySelectorOutput) => {
    setReturnedData(prevReturnedData => ({
      ...prevReturnedData,
      aiFoundrySelection: {
        ...selection,
        aadGroup: prevReturnedData.aiFoundrySelection?.aadGroup ?? '',
      },
    }));
  };

  const handleAadGroupChange = (newValue: unknown) => {
    const aadGroupValue = String(newValue ?? '');
    setReturnedData(prevReturnedData => ({
      ...prevReturnedData,
      aiFoundrySelection: prevReturnedData.aiFoundrySelection
        ? { ...prevReturnedData.aiFoundrySelection, aadGroup: aadGroupValue }
        : { foundry: null, aadGroup: aadGroupValue },
    }));
  };

  return (
    <Grid
      container
      spacing={2}
      direction="row"
      style={{ padding: '0px 10px' }}
      justifyContent="flex-start"
      alignItems="flex-start"
    >
      <Grid item md={12}>
        <Typography variant="h4" aria-label="title">
          American Airlines Information
        </Typography>
      </Grid>
      <Grid item md={3}>
        <SquadInfo
          aria-label="squad-name"
          onChange={values => {
            if (values) {
              setReturnedData(prevReturnedData => ({
                ...prevReturnedData,
                squad: {
                  name: values.name,
                  id: String(values.id),
                },
              }));
            }
          }}
          width="100%"
          required
        />
      </Grid>
      <Grid item md={3}>
        <CmdbAppShortName
          aria-label="short-name"
          onChange={values => {
            setReturnedData(prevReturnedData => ({
              ...prevReturnedData,
              shortName: values.shortName,
              archerId: values.archerId || '',
              cmdbAppFullName: values.cmdbAppFullName || '',
              costCenter: values.costCenter,
            }));
          }}
          value={
            formData?.shortName && formData.archerId && formData.cmdbAppFullName && formData.costCenter
              ? {
                  shortName: formData.shortName,
                  archerId: formData.archerId,
                  cmdbAppFullName: formData.cmdbAppFullName,
                  costCenter: formData.costCenter,
                }
              : undefined
          }
          width="100%"
          required
          getOwnApps
        />
      </Grid>
      {isAIAgentTemplate && (
        <>
          <Grid item md={12}>
            <Typography variant="h4">AI Configuration</Typography>
          </Grid>
          {aiValidationResult?.status === 'loading' && (
            <Grid item md={12}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item>
                  <CircularProgress size={20} aria-label="Validating AI configuration" />
                </Grid>
                <Grid item>
                  <Typography>Validating application for AI resources…</Typography>
                </Grid>
              </Grid>
            </Grid>
          )}
          {aiValidationResult?.status === 'error' && (
            <Grid item md={12}>
              <Alert severity="error">
                {aiValidationResult.errorMessage} Complete{' '}
                <Link
                  href="https://developer.aa.com/create/templates/default/gaas-ai-app-onboard"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', fontWeight: 'bold', textDecoration: 'underline' }}
                >
                  Onboard AI Application to Azure
                </Link>{' '}
                to continue.
              </Alert>
            </Grid>
          )}
          <Grid item md={3}>
            <UseCaseCategory
              value={returnedData.useCaseCategory}
              onChange={value => setReturnedData(prev => ({ ...prev, useCaseCategory: value }))}
            />
          </Grid>
          <Grid item md={3}>
            <AADGroupNameDropdown
              value={returnedData.aiFoundrySelection?.aadGroup ?? ''}
              onChange={handleAadGroupChange}
              error={false}
              helperText=""
              emit="id"
            />
          </Grid>
          <Grid item md={6} />
          <Grid item md={6}>
            <AIFoundrySelector
              onChange={handleAIFoundryChange}
              aiConfigData={aiConfigData as Record<string, any> | null}
              hideAadGroup
            />
          </Grid>
        </>
      )}
      <Grid item md={12}>
        <Typography variant="h4">Repo Information</Typography>
      </Grid>
      {githubDestination.status === 'error' && (
        <Grid item md={12}>
          <Alert severity="error" role="alert">
            {githubDestination.message}
          </Alert>
        </Grid>
      )}
      {destination && (
        <Grid item md={12}>
          <GithubRepoField
            destination={destination}
            onChange={value => {
              setReturnedData(prevReturnedData => ({
                ...prevReturnedData,
                repoName: value,
                containerName: value,
              }));
            }}
            value={returnedData.repoName ?? ''} // formData?.repoName will cause cursor jump bug.
            error={!!error}
          />
        </Grid>
      )}
      <br />
      <Grid item md={6}>
        <GithubRepoDescription
          onChange={value => {
            setReturnedData(prevReturnedData => ({
              ...prevReturnedData,
              repoDescription: value,
            }));
          }}
          value={returnedData.repoDescription ?? ''} // formData?.repoDescription will cause cursor jump bug.
          error={!!error}
        />
      </Grid>
      {destination && (
        <Grid item md={6}>
          <GithubTeamSelect
            destination={destination}
            formData={formData?.githubTeam ?? ''}
            onChange={handleGithubTeamChange}
          />
        </Grid>
      )}
      <Grid item md={12}>
        <Typography variant="h4">Deployment Information</Typography>
      </Grid>
      <Grid item md={12}>
        <DataClassificationGroup
          value={formData?.pci}
          onChange={handleChangePci}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onBlur={() => {}}
          name={formData?.pci ?? ''}
        />
      </Grid>
      <Grid item md={3}>
        <Cluster
          pci={formData?.pci ?? ''}
          value={formData?.cluster ?? ''}
          onChange={values => {
            setReturnedData(prevReturnedData => ({
              ...prevReturnedData,
              cluster: values.target.value,
            }));
          }}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onBlur={() => {}}
          name={formData?.cluster ?? ''}
        />
      </Grid>
      <Grid item md={3}>
        <Namespace
          formData={formData?.namespace ?? ''}
          cluster={formData?.cluster ?? ''}
          onChange={value => {
            setReturnedData(prevReturnedData => ({
              ...prevReturnedData,
              namespace: value,
            }));
          }}
        />
      </Grid>
      <Grid item md={6}>
        <ContainerNameField
          value={returnedData.containerName}
          onChange={value => {
            setReturnedData(prevReturnedData => ({
              ...prevReturnedData,
              containerName: value,
            }));
          }}
        />
      </Grid>
      {isWebsiteTemplate || (
        <Grid item md={12}>
          <CreateIngress value={returnedData.createIngress} onChange={handleChangeAks} />
        </Grid>
      )}
      <Grid item md={12}>
        <div className={classes.sectionHeader}>
          <Typography variant="h4">Hashicorp Vault</Typography>
          <NoPaddingTooltip
            interactive
            title={
              <Alert severity="info">
                Post vault creation info{' '}
                <Link
                  href="https://developer.aa.com/docs/default/component/secret-vault-service/runway/vault-checks/#-post-creation-hashicorp-vault-overview"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="here, post vault creation info (opens in a new tab)"
                >
                  here
                </Link>
              </Alert>
            }
          >
            <IconButton color="primary" size="small" aria-label="vault-info-button">
              <InfoIcon />
            </IconButton>
          </NoPaddingTooltip>
        </div>
      </Grid>
      <Grid item md={6}>
        <HashicorpVault
          key={formData?.shortName}
          vaultNamespace={formData?.vaultNamespace ?? ''}
          vaultPath={formData?.vaultSecretsPath ?? ''}
          onChange={handleHashicorpVault}
          shortName={formData?.shortName}
        />
      </Grid>
      <Grid item md={12} style={{ padding: '40px 10px' }}>
        <Grid item md={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header">
              <Typography variant="h5">Advanced Options</Typography>
              <NoPaddingTooltip
                title={
                  <Alert severity="info">
                    Expand to show more repository customization options. Defaults will apply if no changes are made.
                  </Alert>
                }
              >
                <IconButton color="primary" size="small" aria-label="info-button">
                  <InfoIcon />
                </IconButton>
              </NoPaddingTooltip>
            </AccordionSummary>
            <>
              <Grid item md={12}>
                <Typography variant="h6" style={{ padding: '0px 15px' }}>
                  CICD Workflow
                </Typography>
              </Grid>
              <Grid item md={12}>
                <ReuseableWorkflow value={formData?.rwSelection ?? ''} onChange={handleRWOption} />
              </Grid>
            </>
          </Accordion>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const componentInformationValidation = (
  value: ReturnedData,
  validation: FieldValidation,
  context?: { apiHolder?: ApiHolder; uiSchema?: { 'ui:options'?: Record<string, unknown> } },
) => {
  const githubDestination = getGithubDestination(
    context?.uiSchema?.['ui:options']?.githubOrganization,
    context?.apiHolder,
  );
  if (githubDestination.status === 'error') {
    validation.addError(githubDestination.message);
  }
  if (!value.squad.name || !value.squad.id) {
    validation.addError('Please select a squad');
  }
  if (!value.shortName) {
    validation.addError('Please select a short name');
  }
  repoNameValidation(value.repoName, validation);
  repoDescriptionValidation(value.repoDescription, validation);
  githubTeamValidation(value.githubTeam, validation);
  clusterValidation(value.cluster, validation);
  namespaceValidation(value.namespace, validation);
  containerNameValidation(value.containerName, validation);
  if (!value.vaultNamespace) {
    validation.addError('Please select a Hashicorp Vault namespace');
  }
  if (value.aiFoundrySelection !== null) {
    validateUseCaseCategory(value.useCaseCategory, validation);
    aiFoundrySelectorValidation(value.aiFoundrySelection, validation);
  }
};
